import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import {
  Input, Card, Spinner, Badge,
} from '@/components/ui';
import { IceCream, Search, ChevronRight, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { getRecipeClassification } from '@/lib/recipeTaxonomy';

export default function KitchenListPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: result, isLoading } = useQuery({
    queryKey: ['recipes', { search, limit: 100 }],
    queryFn:  () => recipesApi.list({ page: 1, limit: 100, search: search || undefined }),
  });

  const recipes = result?.data ?? [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <IceCream size={28} className="text-primary" />
              <h1 className="text-2xl font-bold">{t('kitchen.selectRecipe')}</h1>
            </div>
            <Link
              to="/recipes"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={16} />
              {t('kitchen.exitKitchen')}
            </Link>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9 text-lg h-12"
              placeholder={t('kitchen.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Recipe list */}
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        {isLoading ? (
          <Spinner className="py-20" />
        ) : recipes.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            {search ? t('common.noResults') : t('recipes.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {recipes.map(recipe => (
              <Link
                key={recipe.id}
                to={`/kitchen/${recipe.id}`}
                className="block"
              >
                <Card className="transition-colors hover:bg-accent hover:border-primary">
                  <div className="flex items-center gap-4 p-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xl font-semibold truncate">{recipe.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getRecipeClassification(recipe, t).length > 0 && (
                          <Badge variant="secondary" className="text-sm">{getRecipeClassification(recipe, t).join(' · ')}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-muted-foreground">{t('recipes.portions')}</p>
                        <p className="font-semibold">{recipe.multiplier}x batch</p>
                      </div>
                      <ChevronRight size={20} className="text-muted-foreground" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
